/**
 * Proximity Unlock
 *
 * Decides when a player is close enough to a treasure for the leg to complete
 * itself, without typing a keyword. Split in two layers:
 *
 *   1. A pure core (createProximityState / withTarget / evaluateFix) that holds
 *      no globals and is trivial to test.
 *   2. A thin window-event wrapper that listens for 'gpsupdate' and dispatches
 *      'proximitystatus' and 'treasurearrived'. Per CLAUDE.md all sensor data
 *      flows through window events rather than React callbacks.
 */

import { calculateDistance } from './gps.js';

/** Player must be within this many metres of the treasure. */
export const ARRIVAL_RADIUS_M = 10;

/**
 * Reported GPS accuracy must be at least this good. Deliberately looser than
 * the radius: requiring accuracy <= 10 m makes unlocking unreliable under tree
 * cover or between buildings, where the player stands in the right spot and
 * nothing happens.
 */
export const MAX_ACCURACY_M = 20;

/** Number of consecutive qualifying fixes before we accept an arrival. */
export const REQUIRED_CONSECUTIVE_FIXES = 2;

/**
 * Create an empty proximity state.
 * @returns {Object} State with {target, consecutiveFixes}
 */
export function createProximityState() {
  return { target: null, consecutiveFixes: 0 };
}

/**
 * Point the state at a treasure. Always resets the fix counter, so switching
 * target mid-approach cannot carry credit over to the new treasure.
 * @param {Object} state - Current proximity state
 * @param {Object|null} target - Treasure with {id, lat, lng}, or null to clear
 * @returns {Object} New state
 */
export function withTarget(state, target) {
  return { ...state, target: target || null, consecutiveFixes: 0 };
}

/**
 * Evaluate one GPS fix against the current target.
 * @param {Object} state - Current proximity state
 * @param {Object} fix - Location {lat, lng, accuracy}
 * @returns {Object} {state, status, arrivedTreasureId}
 */
export function evaluateFix(state, fix) {
  if (!state.target) {
    return {
      state,
      status: { distance: null, accuracy: fix?.accuracy ?? null, eligible: false, reason: 'no-target' },
      arrivedTreasureId: null,
    };
  }

  const accuracy = typeof fix?.accuracy === 'number' ? fix.accuracy : null;
  const distance = calculateDistance(
    { lat: fix.lat, lng: fix.lng },
    { lat: state.target.lat, lng: state.target.lng }
  );

  // Distance is checked before accuracy: a player far away should be told to
  // walk closer, not blamed for a weak signal.
  if (distance > ARRIVAL_RADIUS_M) {
    return {
      state: { ...state, consecutiveFixes: 0 },
      status: { distance, accuracy, eligible: false, reason: 'too-far' },
      arrivedTreasureId: null,
    };
  }

  if (accuracy === null || accuracy > MAX_ACCURACY_M) {
    return {
      state: { ...state, consecutiveFixes: 0 },
      status: { distance, accuracy, eligible: false, reason: 'poor-accuracy' },
      arrivedTreasureId: null,
    };
  }

  const consecutiveFixes = state.consecutiveFixes + 1;

  if (consecutiveFixes < REQUIRED_CONSECUTIVE_FIXES) {
    return {
      state: { ...state, consecutiveFixes },
      status: { distance, accuracy, eligible: false, reason: 'ok' },
      arrivedTreasureId: null,
    };
  }

  // Arrived. Clear the target so the same treasure cannot fire twice while the
  // consumer is still processing the arrival.
  return {
    state: createProximityState(),
    status: { distance, accuracy, eligible: true, reason: 'arrived' },
    arrivedTreasureId: state.target.id,
  };
}

// --- Window-event wrapper -------------------------------------------------

let watchState = createProximityState();
let gpsListener = null;

function handleGpsUpdate(event) {
  const fix = event?.detail;
  if (!fix) return;

  const result = evaluateFix(watchState, fix);
  watchState = result.state;

  window.dispatchEvent(new CustomEvent('proximitystatus', { detail: result.status }));

  if (result.arrivedTreasureId) {
    window.dispatchEvent(
      new CustomEvent('treasurearrived', { detail: { treasureId: result.arrivedTreasureId } })
    );
  }
}

/**
 * Start reacting to 'gpsupdate' events. Idempotent — calling it twice does not
 * register a second listener.
 */
export function startProximityWatch() {
  if (gpsListener) return;
  gpsListener = handleGpsUpdate;
  window.addEventListener('gpsupdate', gpsListener);
}

/**
 * Stop reacting to 'gpsupdate' events and forget the target.
 */
export function stopProximityWatch() {
  if (gpsListener && typeof window !== 'undefined') {
    window.removeEventListener('gpsupdate', gpsListener);
  }
  gpsListener = null;
  watchState = createProximityState();
}

/**
 * Set the treasure the player is currently walking towards.
 * @param {Object} treasure - {id, lat, lng}
 */
export function setProximityTarget(treasure) {
  watchState = withTarget(watchState, treasure);
}

/**
 * Forget the current target. Fixes still arrive but report 'no-target'.
 */
export function clearProximityTarget() {
  watchState = withTarget(watchState, null);
}
